'use client'

import classNames from 'classnames'
import styles from './AmountSelector.module.scss'
import Button from '@/components/Button/Button'
import Input from '@/components/Input/Input'
import Toggle from '@/components/Toggle/Toggle'
import useI18n from '@/hooks/use-i18n'

export type AmountSelectorProps = {
  className?: string
  id?: string
  name?: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  amounts: (string | undefined)[]
  step?: number
  min?: string
  max?: string
}

const AmountSelector = ({ className, name, value, onChange, amounts, step = 5, min, max }: AmountSelectorProps) => {
  const { i18n } = useI18n()
  const minValue = min ? parseInt(min) : 0
  const maxValue = max ? parseInt(max) : Infinity
  const currentValue = parseInt(value.replace(/[^0-9]/g, '')) || 0

  // Determine if buttons should be disabled
  const isAtMin = currentValue <= minValue
  const isAtMax = currentValue >= maxValue

  const handleAmountChange = (newValue: string) => {
    const event = {
      target: {
        name,
        value: newValue,
      },
    } as React.ChangeEvent<HTMLInputElement>
    onChange(event)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value
    const numericValue = parseInt(inputValue.replace(/[^0-9]/g, '')) || 0

    if (numericValue < minValue) {
      inputValue = `${i18n('currency')}${minValue}`
    } else if (numericValue > maxValue) {
      inputValue = `${i18n('currency')}${maxValue}`
    }

    const constrainedEvent = {
      ...e,
      target: {
        ...e.target,
        value: inputValue,
      },
    } as React.ChangeEvent<HTMLInputElement>

    onChange(constrainedEvent)
  }

  const handleIncrement = () => {
    const numericValue = parseInt(value.replace(/[^0-9]/g, '')) || 0
    const newValue = Math.min(maxValue, numericValue + step)
    handleAmountChange(`${i18n('currency')}${newValue}`)
  }

  const handleDecrement = () => {
    const numericValue = parseInt(value.replace(/[^0-9]/g, '')) || 0
    const newValue = Math.max(minValue, numericValue - step)
    handleAmountChange(`${i18n('currency')}${newValue}`)
  }

  return (
    <div className={classNames(styles.AmountSelector, className)}>
      <div className={styles.inputRow}>
        <Button
          type="button"
          onClick={handleDecrement}
          disabled={isAtMin}
          ariaLabel={i18n('ariaDecrementButton')}
        >
          &ndash;
        </Button>
        <Input
          value={value}
          onChange={handleInputChange}
          readOnly
        />
        <Button
          type="button"
          onClick={handleIncrement}
          disabled={isAtMax}
          ariaLabel={i18n('ariaIncrementButton')}
        >
          +
        </Button>
      </div>
      <Toggle
        name="amount-toggle"
        items={amounts.map(amount => `${i18n('currency')}${amount}`)}
        value={value}
        onChange={onChange}
      />
    </div>
  )
}

export default AmountSelector
